// Display labels and enumerations shared by the API and the UI.

export const JOB_STATUSES = ["booked", "checked_in", "awaiting_approval", "in_progress", "waiting_parts", "ready", "collected", "cancelled"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  booked: "Booked",
  checked_in: "Checked in",
  awaiting_approval: "Awaiting approval",
  in_progress: "In progress",
  waiting_parts: "Waiting for parts",
  ready: "Ready for collection",
  collected: "Collected",
  cancelled: "Cancelled",
};
/** Allowed transitions. Collected and cancelled are terminal (re-opening is an explicit owner action). */
export const JOB_TRANSITIONS: Record<JobStatus, JobStatus[]> = {
  booked: ["checked_in", "cancelled"],
  checked_in: ["awaiting_approval", "in_progress", "cancelled"],
  awaiting_approval: ["in_progress", "cancelled", "ready"],
  in_progress: ["awaiting_approval", "waiting_parts", "ready", "cancelled"],
  waiting_parts: ["in_progress", "awaiting_approval", "cancelled"],
  ready: ["collected", "in_progress"],
  collected: [],
  cancelled: [],
};

export const DEPARTMENTS = ["mechanical", "bodywork"] as const;
export type Department = (typeof DEPARTMENTS)[number];
export const DEPARTMENT_LABEL: Record<Department, string> = { mechanical: "Mechanical", bodywork: "Bodywork" };

export const BOOKING_STATUS_LABEL = { requested: "Requested", confirmed: "Confirmed", arrived: "Arrived", cancelled: "Cancelled", no_show: "No-show" } as const;
export const QUOTE_STATUS_LABEL = { draft: "Draft", sent: "Sent", accepted: "Accepted", declined: "Declined", expired: "Expired", cancelled: "Cancelled" } as const;
export const ENQUIRY_STATUS_LABEL = { new: "New", contacted: "Contacted", quoted: "Quoted", booked: "Booked", closed: "Closed", spam: "Spam" } as const;
export const PAYMENT_METHOD_LABEL = { cash: "Cash", card: "Card", bank_transfer: "Bank transfer", other: "Other" } as const;
export const ATTENDANCE_LABEL = { full: "Full day", half: "Half day", absent: "Absent", holiday_unpaid: "Unpaid leave" } as const;
export const CONTACT_PREF_LABEL = { phone: "Phone call", sms: "Text message", whatsapp: "WhatsApp", email: "Email", none: "Do not contact" } as const;

/** Services the garage does NOT offer — used to warn on enquiries. */
export const EXCLUDED_SERVICES = ["MOT", "tyres", "auto-electrical", "electric-vehicle repairs"];

export function normaliseReg(reg: string): string {
  return reg.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** Format a normalised UK registration for display: "AB12CDE" → "AB12 CDE". */
export function displayReg(reg: string | null | undefined): string {
  if (!reg) return "";
  const r = normaliseReg(reg);
  if (/^[A-Z]{2}\d{2}[A-Z]{3}$/.test(r)) return `${r.slice(0, 4)} ${r.slice(4)}`;
  return r;
}

export function normalisePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  let d = phone.replace(/\D/g, "");
  if (d.startsWith("44")) d = "0" + d.slice(2);
  if (d.startsWith("0044")) d = "0" + d.slice(4);
  return d.length >= 7 ? d : null;
}
